import { useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { FileDown, Loader2, Send, CheckCircle2, XCircle, BellRing, Coins, RotateCcw, Ban, Sparkles, ArrowUpRight, ShieldCheck } from "lucide-react";
import { api, get, post, toastError } from "@/lib/api";
import { nok, fmtDate, fmtDateTime, todayISO } from "@/lib/format";
import { label } from "@/lib/labels";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useAssistantOptional } from "@/context/AssistantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter } from "@/components/ui/sheet";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { BackLink, PageHeader, StatusSteps, Section } from "@/components/common/Page";
import { LoadingState, ErrorState } from "@/components/common/States";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ActivityFeed } from "@/components/common/ActivityFeed";
import { EnumSelect } from "@/components/common/Pickers";
import { Money, Row, Provenance } from "@/components/finance/FinanceBits";
import { invoiceActions, INVOICE_STEPS } from "@/components/finance/money";

const stepOf = (s) => (["betalt", "kreditert"].includes(s) ? "betalt" : ["sendt", "forfalt", "delvis_betalt"].includes(s) ? "sendt" : "utkast");

export default function InvoiceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { can } = useAuth();
  const assistant = useAssistantOptional();
  const q = useQuery({ queryKey: ["finance", "invoice", id], queryFn: () => get(`/finance/invoices/${id}`) });
  const inv = q.data;
  const [pdfBusy, setPdfBusy] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [creditOpen, setCreditOpen] = useState(false);
  const [remindOpen, setRemindOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [rejectOpen, setRejectOpen] = useState(false);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ["finance"] });
    qc.invalidateQueries({ queryKey: ["activities"] });
  };
  const act = useMutation({
    mutationFn: ({ path, body }) => post(`/finance/invoices/${id}/${path}`, body || {}),
    onSuccess: (data, vars) => {
      refresh();
      toast.success(vars.ok || "Oppdatert", { description: vars.desc });
      setPayOpen(false);
      setCreditOpen(false);
      setRemindOpen(false);
      setCancelOpen(false);
      setRejectOpen(false);
      if (vars.path === "credit" && data?.credit_note?.id) navigate(`/okonomi/fakturaer/${data.credit_note.id}`);
    },
    onError: (e) => toastError(e, "Handlingen kunne ikke utføres"),
  });

  const openPdf = async () => {
    setPdfBusy(true);
    try {
      const r = await api.get(`/finance/invoices/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toastError(e, "Kunne ikke lage PDF");
    } finally {
      setPdfBusy(false);
    }
  };

  if (q.isLoading) return <LoadingState className="py-32" />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;

  const isCredit = inv.kind === "kreditnota";
  const actions = invoiceActions(inv, { canWrite: can("finance:write"), canApprove: can("finance:approve") });
  const timeline = [
    ...(inv.status_history || []).map((h) => ({ at: h.at, text: `Status → ${label(h.status)}`, by: h.by, kind: "status" })),
    ...(inv.payments || []).map((p) => ({ at: `${p.date}T12:00:00`, text: `Innbetaling ${nok(p.amount)} (${label(p.method)})${p.reference ? ` · ${p.reference}` : ""}`, by: p.registered_by, kind: "payment" })),
    ...(inv.reminders || []).map((r) => ({ at: `${r.date}T09:00:00`, text: `${r.level}. purring sendt${r.fee ? ` · gebyr ${nok(r.fee)}` : ""}${r.note ? ` · ${r.note}` : ""}`, by: r.sent_by, kind: "reminder" })),
    ...(inv.approval?.approved_at ? [{ at: inv.approval.approved_at, text: `Godkjent${inv.approval.note ? ` · ${inv.approval.note}` : ""}`, by: inv.approval.approved_by_name, kind: "approval" }] : []),
    ...(inv.approval?.requested_at ? [{ at: inv.approval.requested_at, text: `Sendt til godkjenning (over ${nok(inv.approval.threshold)})`, by: inv.approval.requested_by_name, kind: "approval" }] : []),
  ].sort((a, b) => (a.at < b.at ? 1 : -1));

  return (
    <div data-testid="invoice-detail-page">
      <BackLink to="/okonomi/fakturaer" testId="invoice-back-link">Fakturaer</BackLink>
      <PageHeader
        className="mt-2"
        eyebrow={`${isCredit ? "Kreditnota" : "Faktura"} · ${inv.customer_name}${inv.project_name ? ` · ${inv.project_name}` : ""}`}
        title={
          <span className="inline-flex items-baseline gap-3">
            <span className="tabular">{inv.invoice_number}</span>
            <StatusBadge status={inv.status} entity="invoice" variant="pill" />
          </span>
        }
        subtitle={inv.title}
        actions={
          <>
            {assistant && (
              <Button variant="ghost" onClick={() => assistant.ask(`Oppsummer faktura ${inv.invoice_number} – status, betalinger, kundens betalingshistorikk og hva jeg bør gjøre nå`, { invoice_id: inv.id })} data-testid="invoice-ask-ai-button">
                <Sparkles /> Spør AI
              </Button>
            )}
            <Button variant="outline" onClick={openPdf} disabled={pdfBusy} data-testid="invoice-pdf-button">
              {pdfBusy ? <Loader2 className="animate-spin" /> : <FileDown />} PDF
            </Button>
            {actions.includes("approve") && (
              <Button onClick={() => act.mutate({ path: "approve", body: { approve: true }, ok: `${inv.invoice_number} godkjent` })} disabled={act.isPending} data-testid="invoice-approve-button">
                <ShieldCheck /> Godkjenn
              </Button>
            )}
            {actions.includes("reject") && (
              <Button variant="outline" onClick={() => setRejectOpen(true)} data-testid="invoice-reject-button">
                <XCircle /> Avvis
              </Button>
            )}
            {actions.includes("send") && (
              <Button onClick={() => act.mutate({ path: "send", ok: `${inv.invoice_number} sendt`, desc: inv.sent_to ? `Til ${inv.customer?.invoice_email || ""}` : undefined })} disabled={act.isPending} data-testid="invoice-send-button">
                <Send /> Send faktura
              </Button>
            )}
            {actions.includes("request_approval") && (
              <Button onClick={() => act.mutate({ path: "send", ok: "Sendt til godkjenning", desc: `Beløpet er over terskelen på ${nok(inv.approval_threshold)}` })} disabled={act.isPending} data-testid="invoice-request-approval-button">
                <ShieldCheck /> Send til godkjenning
              </Button>
            )}
            {actions.includes("pay") && (
              <Button onClick={() => setPayOpen(true)} data-testid="invoice-pay-button">
                <Coins /> Registrer innbetaling
              </Button>
            )}
            {actions.includes("remind") && (
              <Button variant="outline" onClick={() => setRemindOpen(true)} data-testid="invoice-remind-button">
                <BellRing /> {(inv.reminder_level || 0) + 1}. purring
              </Button>
            )}
            {actions.includes("credit") && (
              <Button variant="outline" onClick={() => setCreditOpen(true)} data-testid="invoice-credit-button">
                <RotateCcw /> Kreditnota
              </Button>
            )}
            {actions.includes("cancel") && (
              <Button variant="ghost" onClick={() => setCancelOpen(true)} data-testid="invoice-cancel-button">
                <Ban /> Annuller
              </Button>
            )}
          </>
        }
        testId="invoice-header"
      />

      {inv.status === "til_godkjenning" && (
        <div className="mt-5 flex items-start gap-3 rounded-xl bg-amber-500/10 px-4 py-3 text-[13px] text-amber-900 dark:text-amber-200" data-testid="invoice-approval-banner">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-medium">Venter på godkjenning.</span> Beløpet {nok(inv.total)} er over terskelen på {nok(inv.approval_threshold)}. Bedt om av {inv.approval?.requested_by_name || "–"} {inv.approval?.requested_at ? fmtDateTime(inv.approval.requested_at) : ""}.
            {!can("finance:approve") && " Kun ledelse/økonomi kan godkjenne."}
          </div>
        </div>
      )}
      {inv.status === "forfalt" && (
        <div className="mt-5 flex items-start gap-3 rounded-xl bg-rose-500/10 px-4 py-3 text-[13px] text-rose-900 dark:text-rose-200" data-testid="invoice-overdue-banner">
          <BellRing className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <span className="font-medium">Forfalt {inv.days_overdue} dager.</span> {nok(inv.outstanding)} utestående{inv.reminder_level ? ` · ${inv.reminder_level} purring${inv.reminder_level > 1 ? "er" : ""} sendt` : " · ingen purring sendt"}.
          </div>
        </div>
      )}

      <div className="mt-8 grid gap-10 lg:grid-cols-12 lg:gap-8">
        <div className="min-w-0 lg:col-span-8">
          {!isCredit && <StatusSteps steps={INVOICE_STEPS} current={stepOf(inv.status)} labelFn={label} className="max-w-md" testId="invoice-steps" />}

          <div className="mt-8 overflow-hidden rounded-2xl ring-1 ring-black/[0.05] dark:ring-white/10" data-testid="invoice-lines">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="bg-muted/50 text-left text-[11.5px] text-muted-foreground">
                  <th className="px-4 py-2 font-medium">Beskrivelse</th>
                  <th className="px-3 py-2 text-right font-medium">Antall</th>
                  <th className="px-3 py-2 font-medium">Enhet</th>
                  <th className="px-3 py-2 text-right font-medium">Pris</th>
                  <th className="px-3 py-2 text-right font-medium">MVA</th>
                  <th className="px-4 py-2 text-right font-medium">Beløp</th>
                </tr>
              </thead>
              <tbody>
                {(inv.lines || []).map((l) => (
                  <tr key={l.id} className="border-t border-border/60" data-testid="invoice-line">
                    <td className="px-4 py-2.5">
                      <div>{l.description}</div>
                      <div className="text-[11.5px] text-muted-foreground">{label(l.kind)}</div>
                    </td>
                    <td className="px-3 py-2.5 text-right tabular">{l.quantity}</td>
                    <td className="px-3 py-2.5 text-muted-foreground">{l.unit}</td>
                    <td className="px-3 py-2.5 text-right tabular">{nok(l.unit_price)}</td>
                    <td className="px-3 py-2.5 text-right tabular text-muted-foreground">{l.vat_pct} %</td>
                    <td className="px-4 py-2.5 text-right font-medium tabular">{nok(l.amount)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t border-border/60 text-[12.5px] text-muted-foreground">
                  <td colSpan={5} className="px-4 py-1.5 pt-3 text-right">Sum eks. mva</td>
                  <td className="px-4 py-1.5 pt-3 text-right tabular">{nok(inv.subtotal)}</td>
                </tr>
                <tr className="text-[12.5px] text-muted-foreground">
                  <td colSpan={5} className="px-4 py-1.5 text-right">MVA</td>
                  <td className="px-4 py-1.5 text-right tabular">{nok(inv.vat_amount)}</td>
                </tr>
                <tr className="text-[15px] font-semibold">
                  <td colSpan={5} className="px-4 py-2 pb-3 text-right">{isCredit ? "Til gode" : "Å betale"}</td>
                  <td className="px-4 py-2 pb-3 text-right tabular" data-testid="invoice-total">{isCredit ? "−" : ""}{nok(inv.total)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          {(inv.paid_amount > 0 || inv.credited_amount > 0) && !isCredit && (
            <div className="mt-4 grid grid-cols-3 gap-4 text-[13px]" data-testid="invoice-settlement">
              <div><div className="text-muted-foreground">Innbetalt</div><Money v={inv.paid_amount} className="font-medium" /></div>
              <div><div className="text-muted-foreground">Kreditert</div><Money v={inv.credited_amount} className="font-medium" /></div>
              <div><div className="text-muted-foreground">Rest å betale</div><Money v={inv.outstanding} className={cn("font-medium", inv.outstanding > 0 && inv.status === "forfalt" && "text-rose-600 dark:text-rose-400")} /></div>
            </div>
          )}

          {inv.notes && <p className="mt-5 text-[13px] text-muted-foreground" data-testid="invoice-notes">{inv.notes}</p>}

          <Section title="Hendelser" className="mt-8" testId="invoice-timeline">
            <ol className="divide-y divide-border/60">
              {timeline.map((t, i) => (
                <li key={i} className="flex items-start gap-3 px-4 py-2.5 text-[13px]" data-testid={`invoice-event-${t.kind}`}>
                  <span className={cn("mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full", t.kind === "payment" ? "bg-emerald-500" : t.kind === "reminder" ? "bg-rose-500" : t.kind === "approval" ? "bg-amber-500" : "bg-foreground/30")} aria-hidden />
                  <span className="min-w-0 flex-1">{t.text}</span>
                  <span className="shrink-0 text-[12px] text-muted-foreground tabular">
                    {t.by ? `${t.by} · ` : ""}
                    {fmtDateTime(t.at)}
                  </span>
                </li>
              ))}
              {!timeline.length && <li className="px-4 py-3 text-[13px] text-muted-foreground">Ingen hendelser enda.</li>}
            </ol>
          </Section>

          <div className="mt-8">
            <ActivityFeed entityType="invoice" entityId={inv.id} allowAdd={can("finance:write")} />
          </div>
        </div>

        <aside className="min-w-0 space-y-8 lg:col-span-4 lg:border-l lg:border-border/70 lg:pl-8" data-testid="invoice-sidebar">
          <div>
            <h2 className="text-[13.5px] font-semibold tracking-tight">Betaling</h2>
            <div className="mt-2 divide-y divide-border/60">
              <Row label="Fakturadato" mono>{fmtDate(inv.issue_date)}</Row>
              {!isCredit && <Row label="Forfall" mono><span className={cn(inv.days_overdue > 0 && "font-medium text-rose-600 dark:text-rose-400")}>{fmtDate(inv.due_date)}{inv.days_overdue > 0 ? ` (+${inv.days_overdue} d)` : ""}</span></Row>}
              <Row label="Betalingsfrist">{inv.payment_terms_days} dager</Row>
              {inv.period_start && <Row label="Periode" mono>{fmtDate(inv.period_start, "d. MMM")} – {fmtDate(inv.period_end, "d. MMM yyyy")}</Row>}
              {!isCredit && <Row label="Utestående" mono><Money v={inv.outstanding} className="font-medium" /></Row>}
              {inv.paid_at && <Row label="Betalt" mono>{fmtDate(inv.paid_at)}</Row>}
              {inv.sent_at && <Row label="Sendt" mono>{fmtDateTime(inv.sent_at)}{inv.sent_to ? ` → ${inv.sent_to}` : ""}</Row>}
              {inv.reminder_level > 0 && <Row label="Purringer">{inv.reminder_level} · siste {fmtDate(inv.last_reminder_at)}</Row>}
            </div>
          </div>
          <div>
            <h2 className="text-[13.5px] font-semibold tracking-tight">Kunde</h2>
            <div className="mt-2 divide-y divide-border/60">
              <Row label="Navn"><Link to={`/kunder/${inv.customer_id}`} className="inline-flex items-center gap-1 hover:underline" data-testid="invoice-customer-link">{inv.customer?.name} <ArrowUpRight className="h-3 w-3 text-muted-foreground" /></Link></Row>
              {inv.customer?.org_number && <Row label="Org.nr" mono>{inv.customer.org_number}</Row>}
              {inv.customer?.invoice_email && <Row label="Faktura-e-post">{inv.customer.invoice_email}</Row>}
              {inv.customer_reference && <Row label="Deres ref">{inv.customer_reference}</Row>}
              {inv.po_number && <Row label="PO" mono>{inv.po_number}</Row>}
            </div>
          </div>
          <div>
            <h2 className="text-[13.5px] font-semibold tracking-tight">Knyttet til</h2>
            <div className="mt-2 divide-y divide-border/60">
              <Row label="Kontrakt">{inv.contract ? <Link to={`/okonomi/kontrakter/${inv.contract.id}`} className="inline-flex items-center gap-1 hover:underline tabular" data-testid="invoice-contract-link">{inv.contract.contract_number} <ArrowUpRight className="h-3 w-3 text-muted-foreground" /></Link> : null}</Row>
              <Row label="Prosjekt">{inv.project ? <Link to={`/okonomi/prosjekter/${inv.project.id}`} className="inline-flex items-center gap-1 hover:underline" data-testid="invoice-project-link">{inv.project.name} <ArrowUpRight className="h-3 w-3 text-muted-foreground" /></Link> : null}</Row>
              {inv.credited_invoice && <Row label="Krediterer"><Link to={`/okonomi/fakturaer/${inv.credited_invoice.id}`} className="hover:underline tabular">{inv.credited_invoice.invoice_number}</Link></Row>}
              {inv.credit_notes?.length > 0 && <Row label="Kreditnotaer">{inv.credit_notes.map((c) => <Link key={c.id} to={`/okonomi/fakturaer/${c.id}`} className="ml-2 hover:underline tabular">{c.invoice_number}</Link>)}</Row>}
            </div>
          </div>
          {inv.approval && (
            <div>
              <h2 className="text-[13.5px] font-semibold tracking-tight">Godkjenning</h2>
              <div className="mt-2 divide-y divide-border/60">
                <Row label="Terskel" mono>{nok(inv.approval.threshold)}</Row>
                <Row label="Bedt om av">{inv.approval.requested_by_name}</Row>
                <Row label="Godkjent av">{inv.approval.approved_by_name || <span className="text-amber-700">venter</span>}</Row>
                {inv.approval.note && <Row label="Merknad">{inv.approval.note}</Row>}
              </div>
            </div>
          )}
          <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
            <Provenance kind="faktisk" /> Registrert i internt økonomiregister · opprettet av {inv.created_by_name || "–"}
          </div>
        </aside>
      </div>

      {/* Payment */}
      <PaymentSheet open={payOpen} onOpenChange={setPayOpen} inv={inv} onSubmit={(body) => act.mutate({ path: "payments", body, ok: "Innbetaling registrert", desc: nok(body.amount) })} busy={act.isPending} />
      <CreditSheet open={creditOpen} onOpenChange={setCreditOpen} inv={inv} onSubmit={(body) => act.mutate({ path: "credit", body, ok: "Kreditnota opprettet" })} busy={act.isPending} />

      <AlertDialog open={remindOpen} onOpenChange={setRemindOpen}>
        <AlertDialogContent data-testid="invoice-remind-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Send {(inv.reminder_level || 0) + 1}. purring?</AlertDialogTitle>
            <AlertDialogDescription>
              Purring på {inv.invoice_number} til {inv.customer?.name} for {nok(inv.outstanding)} utestående ({inv.days_overdue} dager over forfall). Purregebyr på 35 kr legges til fakturaen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="invoice-remind-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => act.mutate({ path: "reminder", body: { add_fee: true }, ok: `${(inv.reminder_level || 0) + 1}. purring sendt` })} data-testid="invoice-remind-confirm">
              Send purring
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={cancelOpen} onOpenChange={setCancelOpen}>
        <AlertDialogContent data-testid="invoice-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Annullere utkastet?</AlertDialogTitle>
            <AlertDialogDescription>{inv.invoice_number} annulleres og kan ikke sendes. Nummeret beholdes i loggen.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Avbryt</AlertDialogCancel>
            <AlertDialogAction onClick={() => act.mutate({ path: "cancel", ok: "Faktura annullert" })} data-testid="invoice-cancel-confirm">Annuller</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <RejectDialog open={rejectOpen} onOpenChange={setRejectOpen} onSubmit={(note) => act.mutate({ path: "approve", body: { approve: false, note }, ok: "Faktura avvist – tilbake til utkast" })} />
    </div>
  );
}

const PaymentSheet = ({ open, onOpenChange, inv, onSubmit, busy }) => {
  const [amount, setAmount] = useState("");
  const [date, setDate] = useState(todayISO());
  const [method, setMethod] = useState("bank");
  const [reference, setReference] = useState("");
  const full = inv?.outstanding || 0;
  const amt = amount === "" ? full : Number(amount);
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[460px] max-w-[94vw]" data-testid="invoice-pay-sheet">
        <SheetHeader>
          <SheetTitle>Registrer innbetaling</SheetTitle>
          <SheetDescription>{inv?.invoice_number} · {nok(full)} utestående. Tomt beløp = fullt oppgjør.</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="pay-amount">Beløp inkl. mva</Label>
            <Input id="pay-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={String(full)} data-testid="invoice-pay-amount" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label htmlFor="pay-date">Dato</Label>
              <Input id="pay-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="invoice-pay-date" />
            </div>
            <div className="space-y-1.5">
              <Label>Metode</Label>
              <EnumSelect value={method} onChange={setMethod} options={["bank", "avtalegiro", "kort", "motregning"]} labelFn={label} testId="invoice-pay-method" />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pay-ref">Referanse / KID</Label>
            <Input id="pay-ref" value={reference} onChange={(e) => setReference(e.target.value)} data-testid="invoice-pay-reference" />
          </div>
          <div className="rounded-xl bg-muted/60 px-3 py-2 text-[12.5px] text-muted-foreground tabular">
            Rest etter innbetaling: <span className="font-medium text-foreground">{nok(Math.max(0, full - amt))}</span>
            {amt >= full - 0.5 ? " · fakturaen merkes betalt" : " · fakturaen merkes delvis betalt"}
          </div>
        </div>
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={() => onSubmit({ amount: amt, date, method, reference: reference || null })} disabled={busy || amt <= 0} data-testid="invoice-pay-submit">Registrer {nok(amt)}</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

const CreditSheet = ({ open, onOpenChange, inv, onSubmit, busy }) => {
  const [reason, setReason] = useState("");
  const [full, setFull] = useState(true);
  const [amount, setAmount] = useState("");
  const remaining = inv ? Number(inv.total) - Number(inv.credited_amount || 0) : 0;
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-[460px] max-w-[94vw]" data-testid="invoice-credit-sheet">
        <SheetHeader>
          <SheetTitle>Kreditnota</SheetTitle>
          <SheetDescription>Lager en kreditnota mot {inv?.invoice_number}. Fakturaen kan krediteres helt eller delvis.</SheetDescription>
        </SheetHeader>
        <div className="mt-5 space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="credit-reason">Årsak (vises på kreditnotaen)</Label>
            <Textarea id="credit-reason" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} placeholder="F.eks. kranbil fikk ikke tilkomst ved retur – kompensasjon iht. avtale" data-testid="invoice-credit-reason" />
          </div>
          <label className="flex items-center gap-2 text-[13px]">
            <Checkbox checked={full} onCheckedChange={(v) => setFull(!!v)} data-testid="invoice-credit-full" /> Krediter hele beløpet ({nok(remaining)})
          </label>
          {!full && (
            <div className="space-y-1.5">
              <Label htmlFor="credit-amount">Beløp inkl. mva</Label>
              <Input id="credit-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} max={remaining} data-testid="invoice-credit-amount" />
            </div>
          )}
        </div>
        <SheetFooter className="mt-6">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Avbryt</Button>
          <Button onClick={() => onSubmit({ reason, amount: full ? null : Number(amount) })} disabled={busy || !reason.trim() || (!full && !(Number(amount) > 0))} data-testid="invoice-credit-submit">Opprett kreditnota</Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

const RejectDialog = ({ open, onOpenChange, onSubmit }) => {
  const [note, setNote] = useState("");
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent data-testid="invoice-reject-dialog">
        <AlertDialogHeader>
          <AlertDialogTitle>Avvis faktura</AlertDialogTitle>
          <AlertDialogDescription>Fakturaen går tilbake til utkast hos den som laget den. Skriv gjerne hva som må endres.</AlertDialogDescription>
        </AlertDialogHeader>
        <Textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Begrunnelse" data-testid="invoice-reject-note" />
        <AlertDialogFooter>
          <AlertDialogCancel>Avbryt</AlertDialogCancel>
          <AlertDialogAction onClick={() => onSubmit(note || null)} data-testid="invoice-reject-confirm">Avvis</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
